import { useEffect, useState } from "react";

import {
  Accordion,
  AccordionActions,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import useLocalization from "../../hooks/useLocalization";

import InfoBox from "../layout/InfoBox";
import AvailabilityDisplay from "./AvailabilityDisplay";
import OptionsDisplay from "./OptionsDisplay";
import ControlledByDisplay from "./ControlledByDisplay";
import Controls from "./Controls";
import VisibilityDisplay from "./VisibilityDisplay";
import VariabilityDisplay from "./VariabilityDisplay";

const SurveyQuestion = ({
  questionIndex,
  question,
  expanded,
  onExpand,
  onEdit,
}) => {
  const theme = useTheme();

  const {
    getLabel,
    getMessage,
    questionTypeLabel,
    getImageResolutionLabel,
    previousAnswerLabel,
  } = useLocalization();

  if (!question) return null;

  return (
    <Accordion
      onChange={(e, isExpanded) => onExpand(questionIndex, isExpanded)}
      expanded={expanded}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: expanded
            ? theme.palette.primary.dark
            : theme.palette.primary.light,
        }}
      >
        <Box sx={{ width: "300px", whiteSpace: "wrap" }}>{question.name}</Box>
      </AccordionSummary>
      <AccordionDetails sx={{ border: "1px solid #ccc" }}>
        <Stack
          spacing={1}
          width="300px"
        >
          <InfoBox label={getLabel("name_fr")}>
            {question.name_fr ? question.name_fr : getMessage("notSet")}
          </InfoBox>

          <Box
            sx={{ display: "flex", justifyContent: "space-between" }}
            gap={1}
          >
            <InfoBox label={getLabel("type")}>
              {questionTypeLabel(question.questionType)}
            </InfoBox>

            {question.questionType === "image" ? (
              <InfoBox label={getLabel("resolution")}>
                {getImageResolutionLabel(question?.config?.resolution)}
              </InfoBox>
            ) : null}
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "space-between" }}
            gap={1}
          >
            <InfoBox label={getLabel("active")}>
              {question.isActive ? "True" : "False"}
            </InfoBox>
            <InfoBox label={getLabel("isRequired")}>
              {question.isRequired ? "True" : "False"}
            </InfoBox>
            <InfoBox
              label={getLabel("previousAnswer")}
              flex={2}
            >
              {previousAnswerLabel(question.previousAnswer)}
            </InfoBox>
          </Box>

          <InfoBox label={getLabel("availability")}>
            <AvailabilityDisplay
              value={question.availability}
              emptyMessage={getMessage("basedOnSection")}
            />
          </InfoBox>

          {question.questionType === "radio" ||
          question.questionType === "checkbox" ||
          question.questionType === "select" ||
          question.questionType === "multiselect" ? (
            <InfoBox label={getLabel("options")}>
              <OptionsDisplay options={question.options} />
            </InfoBox>
          ) : null}

          <InfoBox label={getLabel("controlledBy")}>
            <ControlledByDisplay value={question.controlledBy} />
          </InfoBox>

          <InfoBox label={getLabel("visibility")}>
            <VisibilityDisplay value={question.visibility} />
          </InfoBox>

          <InfoBox label={getLabel("variability")}>
            <VariabilityDisplay value={question.variability} />
          </InfoBox>

          <Controls surveyQuestionId={question.id} />
        </Stack>
      </AccordionDetails>

      <AccordionActions>
        <Button
          onClick={() => onEdit(questionIndex)}
          variant="contained"
          size="small"
        >
          {getLabel("edit")}
        </Button>
      </AccordionActions>
    </Accordion>
  );
};

export default SurveyQuestion;
